import type { LanguageCode } from "common/types/languageCode";
import { unsafelyKeys } from "common/utilities/object";
import type { EntityType, TrackMeta, TrackPage } from "domains/analytics/types";

// Add the default expected tracking metadata for events that can happen on multiple pages
export const buildTrackingData = (siteLanguage: LanguageCode, trackData?: Partial<TrackMeta>) => ({
  ...getAdditionalData(trackData),
  ...trackData,
  client: "web-app",
  language: siteLanguage,
});

const getAdditionalData = (trackData?: Partial<TrackMeta>) => {
  const { pathname } = location;
  const activeTopicPath = topicPaths.find((path) => pathname.startsWith(`/${path}/`));

  if (activeTopicPath !== undefined) {
    let page: TrackPage = activeTopicPath === "create" ? "Create" : "Topic detail";
    const topicId = (pathname.split(`/${activeTopicPath}/`)[1] ?? "").split("/")[0];
    const keyIssueId = (pathname.split("/key-issues/")[1] ?? "").split("/")[0];

    const subpageRouteSlug = unsafelyKeys(topicSubpages).find((routeSlug) => pathname.includes(`/${routeSlug}`));
    let subpage: TrackPage | undefined = undefined;
    let entityId = topicId;
    let entityType: EntityType = "Topic";
    let secondaryEntityType: EntityType | undefined = undefined;
    let secondaryEntityId = undefined;
    let subpageDetails: (typeof topicSubpages)[keyof typeof topicSubpages] | undefined = undefined;
    const selectedTopicId = keyIssueId ?? topicId;
    if (subpageRouteSlug) {
      // We're on a /topics/:id/publications|videos|data... route
      subpageDetails = topicSubpages[subpageRouteSlug];
      if (subpageDetails) {
        const pathParts = pathname.split(`/${subpageRouteSlug}/`);

        if (pathParts.length > 1 && pathParts[1]) {
          // We're on a detail route, so use the related item as the main entity
          // and the topic as the secondary entity
          subpage = page;
          page = subpageDetails[1];
          entityId = pathParts[1];
          entityType = subpageDetails[2];
          secondaryEntityType = "Topic";
          secondaryEntityId = selectedTopicId;
        } else {
          // We're on a list route, so use the topic as the main entity
          subpage = subpageDetails[0];
        }
      }
    }

    if (!secondaryEntityId && keyIssueId) {
      // If we don't have a second entity, then use the key issue
      secondaryEntityId = keyIssueId;
      secondaryEntityType = "Topic";
    }

    if (trackData?.entityId && !trackData?.secondaryEntityId && trackData.entityId !== selectedTopicId) {
      // If we already have an entity id as part of the action, then add our selected topic as the second entity
      secondaryEntityId = selectedTopicId;
      secondaryEntityType = "Topic";
    }

    return { entityId, entityType, page, secondaryEntityId, secondaryEntityType, subpage } as const;
  }

  if (pathname === "/") return { page: "Landing" } as const;

  const highLevelPath = unsafelyKeys(highLevelPaths).find((path) => pathname.startsWith(`/${path}`));
  if (highLevelPath !== undefined) return { page: highLevelPaths[highLevelPath] } as const;

  return undefined;
};

const topicPaths = ["discover", "topics", "create"] as const;

const topicSubpages = {
  publications: ["Publications List", "Knowledge detail", "Publication"],
  videos: ["Video List", "Video detail", "Video"],
  "interactive-data": ["Interactive Data List", "Interactive Data Detail", "Interactive data"],
  events: ["Event List", "Session detail", "Session"],
  initiatives: ["Initiative List", "Project detail", "Project"],
  stakeholders: ["Stakeholder List", "Stakeholder detail", "Stakeholder"],
} as const;

const highLevelPaths = {
  discover: "Discover",
  topics: "Discover",
  create: "Create landing",
  monitor: "Monitor",
  about: "About",
  search: "Search",
  "terms-and-conditions": "Terms and conditions",
  "privacy-policy": "Terms and conditions",
  "cookie-notice": "Terms and conditions",
} as const;
